import { useState } from "react";
import { api, getApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Hash } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  "intake", "docs_review", "audit", "counterclaim_filed", "trustee_pledge", "settlement", "completed",
];

export default function AppRow({ app, onUpdate }) {
  const [status, setStatus] = useState(app.status);
  const [note, setNote] = useState("");
  const [tracking, setTracking] = useState(app.tracking_number || "");
  const [open, setOpen] = useState(false);

  const save = async () => {
    try {
      await api.patch(`/applications/${app.application_id}/status`, {
        status, note, tracking_number: tracking || null,
      });
      toast.success("Application updated");
      setOpen(false);
      onUpdate();
    } catch (e) { toast.error(getApiError(e)); }
  };

  return (
    <Card className="border-black/5" data-testid={`admin-app-${app.application_id}`}>
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-medium">{app.full_name} · {app.email}</div>
            <div className="text-xs text-[color:var(--text-secondary)]">
              {app.property_address}, {app.property_city}, {app.property_state} {app.property_zip}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-black/10 capitalize">{app.status.replace("_", " ")}</Badge>
            {app.tracking_number && <Badge className="bg-[color:var(--bright-green)] text-white"><Hash className="h-3 w-3 mr-1" />{app.tracking_number}</Badge>}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="border-black/10" data-testid={`admin-app-edit-${app.application_id}`}>Update</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-serif">Update case</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Status</Label>
                    <Select value={status} onValueChange={setStatus}>
                      <SelectTrigger data-testid="admin-app-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Manual tracking number (member sees this when set)</Label>
                    <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="e.g. AGG-2026-00123" data-testid="admin-app-tracking-input" />
                  </div>
                  <div>
                    <Label>Note (added to history)</Label>
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="admin-app-note-input" />
                  </div>
                  <Button onClick={save} className="w-full bg-[color:var(--fire-red)] hover:bg-[color:var(--fire-red-hover)] text-white" data-testid="admin-app-save-btn">Save</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-3 mt-4 text-xs text-[color:var(--text-secondary)]">
          <div><strong>Lender:</strong> {app.lender_name}</div>
          <div><strong>Mortgage bal:</strong> ${Number(app.mortgage_balance).toLocaleString()}</div>
          <div><strong>1.5% fee:</strong> ${Number(app.guild_service_fee ?? app.guild_fee_8pct ?? 0).toLocaleString()}</div>
          <div><strong>Court:</strong> {app.court_name || "—"}</div>
          <div><strong>Sheriff sale:</strong> {app.sheriff_sale_scheduled ? (app.sheriff_sale_date || "yes") : "no"}</div>
          <div><strong>SEC bundled:</strong> {app.sec_bundled ? "yes" : "no"}</div>
        </div>
      </CardContent>
    </Card>
  );
}
